import { apiUrl, getJson, postJson } from '@/lib/api';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Globe,
  CalendarDays,
  MonitorPlay,
  Clapperboard,
  MapPin,
  Languages,
  Calendar,
  ArrowUpDown,
  Search,
  BadgeCheck,
} from 'lucide-react';
import { Input, Tooltip } from '@parrot/ui';
import { PortfolioPicker } from '@/components/PortfolioPicker';
import { usePortfolios, usePortfolioMembers } from '@/hooks/usePortfolios';
import {
  Select,
  PlatformIcon,
  MetricCell,
  Spinner,
  Delta,
  SplitBar,
} from '@/components/primitives';
import type { MetricTone, SelectOption } from '@/components/primitives';
import { ChartTooltip } from '@/components/organisms/ChartTooltip';
import { TitleCell } from '@/components/organisms/TitleCell';
import {
  ColumnPicker,
  AddFilterChip,
  DismissibleFilter,
  LeaderboardTable,
  SegmentedControl,
} from '@/components/organisms';
import type { SecondaryFilter } from '@/components/organisms';
import { shortPlatform, prettyQuarter } from '@/lib/format';
import styles from './Leaderboard.module.css';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface LeaderboardEntry {
  parrot_id: string;
  title: string;
  content_type: string;
  availability_platform: string;
  /** All platforms this title streams on (aggregate mode). */
  platforms?: string[];
  /** Per-platform revenue split for the inline bar + tooltip, biggest first. */
  revenue_by_platform?: { platform: string; revenue: number }[];
  revenue_contribution_usd: number;
  revenue_share: number;
  title_rank: number;
  main_genre?: string;
  sub_genre?: string;
  release_year?: number;
  subscribers_acquired?: number;
  subscribers_retained?: number;
  acquired_usd?: number;
  retained_usd?: number;
  subscribers_equivalent?: number;
  is_exclusive?: boolean;
  original_country?: string;
  original_language?: string;
  demand_label?: string;
  imdb_rating?: number;
  total_episodes?: number;
  tv_series_type?: string;
  /** Null when the scope is all-time — there is no previous period to compare. */
  revenue_change_pct?: number | null;
}

interface FilterOptions {
  platforms: string[];
  quarters: string[];
  regions: string[];
  contentTypes: string[];
  genres: string[];
  countries: string[];
  languages: string[];
  releaseYears: { min: number | null; max: number | null };
}

interface Meta {
  quarter: string;
  count: number;
  offset: number;
  hasMore: boolean;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

/** Rows per request. Pages are appended as the sentinel scrolls into view. */
const PAGE_SIZE = 50;
const ALL_TIME = 'all';

/** Row-1 content-type choice. The API only ever has shows and movies. */
const CONTENT_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'show', label: 'TV Shows' },
  { value: 'movie', label: 'Movies' },
];
const CONTENT_TYPE_NOUN: Record<string, string> = { show: 'TV shows', movie: 'movies' };
/** Columns that only mean something for shows — hidden when Movies is chosen. */
const SHOW_ONLY_COLUMNS = new Set(['episodes', 'seriesType']);

/** Must stay in step with SORTABLE_FIELDS in queries.py. */
type SortField =
  | 'title'
  | 'revenue_contribution_usd'
  | 'revenue_share'
  | 'availability_platform'
  | 'subscribers_acquired'
  | 'subscribers_retained'
  | 'revenue_change_pct'
  | 'release_year';

type SortDir = 'asc' | 'desc';

/**
 * Column registry. Every column the table can show is declared once here with
 * its width, alignment, sort field and renderer; the picker decides which are
 * visible and in what order. Adding a metric means adding one entry.
 */
interface ColumnDef {
  id: string;
  label: string;
  width: string;
  align: 'left' | 'right';
  sortField?: SortField;
  tone?: MetricTone;
  /** Always visible, never reordered. */
  locked?: boolean;
  /** Shown before the user customises anything. */
  defaultVisible?: boolean;
  /** Column max across loaded rows, for the magnitude bar. */
  magnitude?: (e: LeaderboardEntry) => number | undefined;
  /** Explains how the metric is derived. Shown on hover in the header. */
  help?: string;
}

const COLUMN_DEFS: ColumnDef[] = [
  { id: 'rank', label: '#', width: '44px', align: 'left', locked: true },
  { id: 'title', label: 'Title', width: '340px', align: 'left', sortField: 'title', locked: true },

  {
    id: 'platform',
    label: 'Platform',
    width: '240px',
    align: 'left',
    sortField: 'availability_platform',
    defaultVisible: true,
  },
  {
    id: 'revenue',
    label: 'Revenue',
    width: '150px',
    align: 'left',
    sortField: 'revenue_contribution_usd',
    tone: 'revenue',
    defaultVisible: true,
    magnitude: (e) => e.revenue_contribution_usd,
    help: 'Revenue attributed to this title, summed across every region and quarter in the current selection.',
  },
  {
    id: 'share',
    label: 'Share',
    width: '120px',
    align: 'left',
    sortField: 'revenue_share',
    tone: 'share',
    defaultVisible: true,
    magnitude: (e) => e.revenue_share,
    help: "This title's revenue as a share of its platform's revenue for the same content type. Shows and movies are measured against separate totals, so a movie's share is not directly comparable to a show's. Scoped to the selected region and period.",
  },
  {
    id: 'acquired',
    label: 'Acquired Users',
    width: '150px',
    align: 'left',
    sortField: 'subscribers_acquired',
    tone: 'acquired',
    defaultVisible: true,
    magnitude: (e) => e.subscribers_acquired,
    help: 'Subscribers whose sign-up is attributed to this title.',
  },
  {
    id: 'retained',
    label: 'Retained Users',
    width: '150px',
    align: 'left',
    sortField: 'subscribers_retained',
    tone: 'retained',
    defaultVisible: true,
    magnitude: (e) => e.subscribers_retained,
    help: 'Existing subscribers whose continued subscription is attributed to this title.',
  },
  {
    id: 'qoq',
    label: 'QoQ',
    width: '104px',
    align: 'left',
    sortField: 'revenue_change_pct',
    defaultVisible: true,
    help: 'Change in revenue against the previous quarter. Blank when the selection has no single preceding quarter, such as All Time.',
  },

  {
    id: 'acquiredRev',
    label: 'Acquired Revenue',
    width: '140px',
    align: 'left',
    tone: 'acquired',
    magnitude: (e) => e.acquired_usd,
    help: "The portion of this title's revenue attributed to acquiring new subscribers.",
  },
  {
    id: 'retainedRev',
    label: 'Retained Revenue',
    width: '140px',
    align: 'left',
    tone: 'retained',
    magnitude: (e) => e.retained_usd,
    help: "The portion of this title's revenue attributed to retaining existing subscribers.",
  },
  {
    id: 'split',
    label: 'Acq / Ret Split',
    width: '150px',
    align: 'left',
    help: "How this title's revenue divides between acquisition and retention.",
  },
  {
    id: 'subsEquivalent',
    label: 'Subscriber Equiv.',
    width: '140px',
    align: 'left',
    magnitude: (e) => e.subscribers_equivalent,
    help: "Subscriber-equivalents: the subscriber count this title's engagement corresponds to.",
  },
  { id: 'genre', label: 'Genre', width: '120px', align: 'left' },
  { id: 'subGenre', label: 'Sub-genre', width: '120px', align: 'left' },
  { id: 'contentType', label: 'Content Type', width: '116px', align: 'left' },
  {
    id: 'releaseYear',
    label: 'Release Year',
    width: '112px',
    align: 'left',
    sortField: 'release_year',
  },
  { id: 'country', label: 'Country', width: '140px', align: 'left' },
  { id: 'language', label: 'Language', width: '130px', align: 'left' },
  {
    id: 'imdb',
    label: 'IMDb',
    width: '90px',
    align: 'left',
    help: 'IMDb user rating, where available.',
  },
  {
    id: 'episodes',
    label: 'Episodes',
    width: '104px',
    align: 'left',
    help: 'Total episodes recorded for the title.',
  },
  { id: 'seriesType', label: 'Series Type', width: '120px', align: 'left' },
  {
    id: 'exclusive',
    label: 'Exclusive',
    width: '104px',
    align: 'left',
    help: 'Whether the title is exclusive to this platform in the selected scope.',
  },
];

const DEFAULT_ORDER = COLUMN_DEFS.map((c) => c.id);
const DEFAULT_VISIBLE = COLUMN_DEFS.filter((c) => c.defaultVisible).map((c) => c.id);

// ─── API ───────────────────────────────────────────────────────────────────────

/** Every filter the leaderboard understands. '' always means unfiltered. */
export interface LeaderboardFilters {
  platform: string[];
  region: string[];
  quarter: string;
  /** '' = all titles, else 'show' | 'movie'. */
  contentType: string;
  genre: string[];
  exclusivity: string;
  country: string[];
  language: string[];
  yearMin: string;
  yearMax: string;
  /** Free-text title search (hosted in the Title column header). */
  search: string;
}

async function fetchLeaderboard(
  filters: LeaderboardFilters,
  sort: SortField,
  dir: SortDir,
  offset: number,
  /** Member parrot_ids of the selected portfolio; undefined → no portfolio scope. */
  portfolios?: string[],
): Promise<{ data: LeaderboardEntry[]; meta: Meta }> {
  const {
    quarter,
    platform,
    region,
    contentType,
    genre,
    country,
    language,
    exclusivity,
    yearMin,
    yearMax,
    search: titleSearch,
  } = filters;

  // POST path: a portfolio is selected. The member id list can be large (far
  // past what fits in a URL/query string), so filters go in a JSON body and the
  // ids ride along in `portfolios`. Same { data, meta } envelope back.
  if (portfolios && portfolios.length) {
    return postJson(apiUrl('/leaderboard'), {
      portfolios,
      timePeriod: quarter || 'latest',
      platform,
      region,
      contentType,
      genre,
      exclusivity,
      country,
      language,
      yearMin,
      yearMax,
      search: titleSearch,
      sort,
      dir,
      limit: PAGE_SIZE,
      offset,
    });
  }

  // GET path (unchanged): no portfolio selected → the global leaderboard.
  const search = new URLSearchParams();
  // Multi-value filters → comma-joined; the backend reads them as IN(...) lists.
  const setMulti = (key: string, values: string[]) => {
    if (values.length) search.set(key, values.join(','));
  };
  setMulti('platform', platform);
  setMulti('region', region);
  if (contentType) search.set('contentType', contentType);
  setMulti('genre', genre);
  setMulti('country', country);
  setMulti('language', language);
  if (exclusivity) search.set('exclusivity', exclusivity);
  if (yearMin) search.set('yearMin', yearMin);
  if (yearMax) search.set('yearMax', yearMax);
  if (titleSearch) search.set('search', titleSearch);
  // timePeriod drives both the single-quarter and cumulative code paths.
  search.set('timePeriod', quarter || 'latest');
  search.set('sort', sort);
  search.set('dir', dir);
  search.set('limit', String(PAGE_SIZE));
  search.set('offset', String(offset));

  return getJson(apiUrl(`/leaderboard?${search}`));
}

/** /api/titles/filters does not exist — the options live on the leaderboard
 *  blueprint and come back unwrapped, not nested under `data`. */
async function fetchFilters(): Promise<FilterOptions> {
  return getJson(apiUrl('/leaderboard/filters'));
}

// ─── Formatters ────────────────────────────────────────────────────────────────

function formatRevenue(value: number): string {
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  return `$${(value / 1_000).toFixed(0)}K`;
}

function formatUsers(value: number | undefined): string {
  if (!value) return '—';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toFixed(0);
}

/** EXCEPTIONAL -> Exceptional, for the tier pill's class suffix. */
/** Raw slugs ("game-show", "sci_fi") → display words ("Game Show", "Sci Fi"). */
const titleCase = (v: string) =>
  v ? v.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : v;

function tierKey(label: string): string {
  const normalised = label.replace(/[^a-z]/gi, '').toLowerCase();
  return normalised.charAt(0).toUpperCase() + normalised.slice(1);
}

function formatShare(value: number): string {
  return `${(value * 100).toFixed(2)}%`;
}

/** Above this, the previous quarter was near zero and the exact % is noise. */
const QOQ_CAP = 999;

/**
 * Inline revenue cell. Single platform → the gold magnitude bar (unchanged
 * look). Multiple platforms → the same 3px magnitude track, but the filled
 * portion is split into per-platform segments (largest first), so bar length
 * still means "total revenue vs the column max" on every row. Segments are gold
 * shades by revenue rank. Hovering the cell lists each platform's revenue + total.
 */
/* Revenue keeps one tone per column (gold), so platform segments are shades of
   gold by revenue rank rather than separate hues — no clash with the Share /
   Acquired / Retained bars next door. */
const GOLD_SHADES = [100, 70, 50, 36, 26, 18];
function revenueShade(rank: number): string {
  const pct = GOLD_SHADES[Math.min(rank, GOLD_SHADES.length - 1)];
  return `color-mix(in srgb, var(--accent-secondary) ${pct}%, transparent)`;
}

/** Platforms ordered by their revenue for this title (highest first). */
function platformsByRevenue(
  plats: string[],
  split?: { platform: string; revenue: number }[],
): string[] {
  if (!split?.length) return plats;
  const rev = new Map(split.map((p) => [p.platform, p.revenue]));
  return [...plats].sort((a, b) => (rev.get(b) ?? -1) - (rev.get(a) ?? -1));
}

function RevenueSplitCell({
  total,
  split,
  fraction,
}: {
  total: number;
  split?: { platform: string; revenue: number }[];
  fraction?: number;
}) {
  const parts = (split ?? [])
    .filter((p) => p.revenue > 0)
    .sort((a, b) => b.revenue - a.revenue)
    .map((p, i) => ({ ...p, color: revenueShade(i) }));

  // Single platform (or no breakdown): keep the gold magnitude bar.
  if (parts.length <= 1) {
    return <MetricCell value={formatRevenue(total)} fraction={fraction} tone="revenue" />;
  }

  const tooltipContent = (
    <ChartTooltip
      variant="bare"
      rows={parts.map((p) => ({
        label: shortPlatform(p.platform),
        value: formatRevenue(p.revenue),
        color: p.color,
      }))}
      totals={[{ label: 'Total', value: formatRevenue(total) }]}
    />
  );

  return (
    <span className={styles.revenueCell}>
      <Tooltip title="Revenue by platform" content={tooltipContent} placement="bottom">
        <span className={styles.revenueInner}>
          <span className={styles.revenueValue}>{formatRevenue(total)}</span>
          <SplitBar
            gapped
            extent={fraction ?? 1}
            className={styles.splitBar}
            segments={parts.map((p) => ({
              fraction: total ? p.revenue / total : 0,
              color: p.color,
            }))}
          />
        </span>
      </Tooltip>
    </span>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

export function Leaderboard() {
  // Links can open the leaderboard pre-filtered (?platform=Netflix&region=UCAN&contentType=show),
  // e.g. "Full leaderboard" from Home or a platform profile. Read once, as the starting state.
  const [searchParams] = useSearchParams();
  const listParam = (key: string) => searchParams.get(key)?.split(',').filter(Boolean) ?? [];
  const [filters, setFilters] = useState<LeaderboardFilters>(() => ({
    platform: listParam('platform'),
    region: listParam('region'),
    quarter: '',
    contentType: searchParams.get('contentType') ?? '',
    genre: [],
    exclusivity: '',
    country: [],
    language: [],
    yearMin: '',
    yearMax: '',
    search: '',
  }));
  const [sortField, setSortField] = useState<SortField>('revenue_contribution_usd');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // ── Portfolio scope ────────────────────────────────────────────────────────
  // Single-select, opt-in: nothing is selected by default. When a portfolio is
  // chosen we resolve its member parrot_ids and the leaderboard query switches
  // from the global GET to the POST contract (member ids in the body).
  const userEmail = localStorage.getItem('user_email');
  const {
    portfolios,
    pending: portfoliosPending,
    error: portfoliosError,
    resolveMembers,
  } = usePortfolios(userEmail);
  const [portfolioId, setPortfolioId] = useState<string | undefined>(undefined);
  const selectedIds = useMemo(() => (portfolioId ? [portfolioId] : []), [portfolioId]);
  const {
    memberIds,
    empty: portfolioEmpty,
    tooLarge: portfolioTooLarge,
    query: membersQuery,
  } = usePortfolioMembers({ resolveMembers }, selectedIds);

  const [columnOrder, setColumnOrder] = useState<string[]>(DEFAULT_ORDER);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE);
  /** Which secondary (refine) filters are currently surfaced in the bar. */
  const [activeSecondary, setActiveSecondary] = useState<string[]>([]);

  const orderedColumns = useMemo(
    () =>
      columnOrder
        .map((id) => COLUMN_DEFS.find((c) => c.id === id))
        .filter((c): c is ColumnDef => Boolean(c)),
    [columnOrder],
  );

  const shownColumns = useMemo(
    () =>
      orderedColumns.filter(
        (c) =>
          (c.locked || visibleColumns.includes(c.id)) &&
          // Content type is fixed by the Row-1 control once chosen; show-only
          // columns have nothing to say about movies.
          !(filters.contentType && c.id === 'contentType') &&
          !(filters.contentType === 'movie' && SHOW_ONLY_COLUMNS.has(c.id)),
      ),
    [orderedColumns, visibleColumns, filters.contentType],
  );

  /** Single-value filters (quarter, exclusivity, year bounds, search). */
  const set =
    (key: 'quarter' | 'contentType' | 'exclusivity' | 'yearMin' | 'yearMax' | 'search') =>
    (value: string) =>
      setFilters((prev) => ({ ...prev, [key]: value }));

  /** Multi-value filters (platform, region, genre, country, language). */
  const setArr =
    (key: 'platform' | 'region' | 'genre' | 'country' | 'language') => (values: string[]) =>
      setFilters((prev) => ({ ...prev, [key]: values }));

  /**
   * Clears the custom-portfolio scope and returns the leaderboard to
   * content-type mode. A portfolio and the content-type control are mutually
   * exclusive ways to scope the board, so clearing the portfolio also resets
   * the content-type segmented control to its default "All" (contentType = '')
   * — both so the control re-enables in a clean state and because the
   * content-type value is ignored while a portfolio is selected anyway.
   *
   * Shared by both clear affordances: the PortfolioPicker's ✕ (via `onClear`)
   * and the "Clear portfolio" button in the disabled SegmentedControl's tooltip.
   */
  const clearPortfolio = useCallback(() => {
    setPortfolioId(undefined);
    set('contentType')('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: filtersData } = useQuery({
    queryKey: ['filters'],
    queryFn: fetchFilters,
    staleTime: 10 * 60 * 1000,
  });

  /**
   * Sorting lives in the query key, so changing a column refetches from the
   * server. Sorting client-side would only reorder the rows already paged in,
   * which silently misrepresents a 2,000-row result set.
   *
   * Portfolio scope: with a portfolio selected the query key includes its
   * resolved member ids and the fetch takes the POST path. We hold the query
   * until members have resolved (and skip it entirely when the portfolio is
   * empty or over the id cap) so we never fire an unscoped request by mistake.
   */
  const portfolioActive = selectedIds.length > 0;
  const portfolioReady = !portfolioActive || memberIds !== undefined;
  const leaderboardEnabled =
    portfolioReady && !portfolioTooLarge && !(portfolioActive && portfolioEmpty);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['leaderboard', filters, sortField, sortDir, memberIds ?? null],
      initialPageParam: 0,
      queryFn: ({ pageParam }) =>
        fetchLeaderboard(filters, sortField, sortDir, pageParam as number, memberIds),
      getNextPageParam: (lastPage) =>
        lastPage.meta.hasMore ? lastPage.meta.offset + lastPage.data.length : undefined,
      staleTime: 5 * 60 * 1000,
      enabled: leaderboardEnabled,
    });

  const isAllTime = filters.quarter === ALL_TIME;
  const meta = data?.pages[0]?.meta;

  // Members still resolving for a selected portfolio — treat as loading so the
  // table shows its skeleton rather than a spurious "no titles" empty state.
  const isResolvingMembers = portfolioActive && membersQuery.isPending && !portfolioTooLarge;
  const tableLoading = isLoading || isResolvingMembers;

  const rows = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);

  // Load the next page when the sentinel below the table scrolls into view.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const onSentinel = useCallback((node: HTMLDivElement | null) => {
    sentinelRef.current = node;
  }, []);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { rootMargin: '400px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, rows.length]);

  /** Per-column maxima so every bar in a column shares one yardstick. */
  const maxima = useMemo(() => {
    const out: Record<string, number> = {};
    for (const col of COLUMN_DEFS) {
      if (!col.magnitude) continue;
      out[col.id] = rows.reduce((acc, r) => Math.max(acc, col.magnitude!(r) ?? 0), 0) || 1;
    }
    return out;
  }, [rows]);

  /** Sort-by dropdown: pick a field, default its direction sensibly. */
  const onSortSelect = (value: string) => {
    const field = value as SortField;
    setSortField(field);
    setSortDir(field === 'title' || field === 'availability_platform' ? 'asc' : 'desc');
  };

  /** Header-click sort from the shared table: key is the API sort field. */
  const onTableSort = ({ key, direction }: { key: string; direction: SortDir }) => {
    setSortField(key as SortField);
    setSortDir(direction);
  };

  // ─── Filter options ────────────────────────────────────────────────────────

  const opts = filtersData;

  /** Turn ISO codes into readable names without shipping a lookup table. */
  const regionNames = useMemo(() => new Intl.DisplayNames(['en'], { type: 'region' }), []);
  const languageNames = useMemo(() => new Intl.DisplayNames(['en'], { type: 'language' }), []);
  const safeName = (fn: Intl.DisplayNames, code: string) => {
    try {
      return fn.of(code) ?? code;
    } catch {
      return code;
    }
  };

  const platformOptions = [
    { value: '', label: 'All Platforms' },
    ...(opts?.platforms?.map((p) => ({
      value: p,
      label: shortPlatform(p),
      icon: <PlatformIcon name={p} size={20} />,
    })) || []),
  ];

  const regionOptions = [
    { value: '', label: 'All Regions' },
    ...(opts?.regions?.map((r) => ({ value: r, label: r })) || []),
  ];

  /* The latest-quarter option keeps value '' (the API default), but its label
     spells out which quarter that is, e.g. 'Latest (Q2 2026)'. */
  const latestQuarterLabel = opts?.quarters?.[0]
    ? `Latest (${prettyQuarter(opts.quarters[0])})`
    : 'Latest Quarter';
  const quarterOptions = [
    { value: '', label: latestQuarterLabel },
    { value: ALL_TIME, label: 'All Time' },
    ...(opts?.quarters?.map((q) => ({ value: q, label: prettyQuarter(q) })) || []),
  ];

  const years = (() => {
    const min = opts?.releaseYears?.min;
    const max = opts?.releaseYears?.max;
    if (!min || !max) return [];
    return Array.from({ length: max - min + 1 }, (_, i) => String(max - i));
  })();

  // ─── Refine (secondary) filter options ──────────────────────────────────────

  const genreOptions: SelectOption[] = [
    { value: '', label: 'All Genres' },
    ...(opts?.genres?.map((g) => ({ value: g, label: titleCase(g) })) || []),
  ];
  const countryOptions: SelectOption[] = [
    { value: '', label: 'All Countries' },
    ...(opts?.countries?.map((c) => ({ value: c, label: safeName(regionNames, c) })) || []),
  ];
  const languageOptions: SelectOption[] = [
    { value: '', label: 'All Languages' },
    ...(opts?.languages?.map((l) => ({ value: l, label: safeName(languageNames, l) })) || []),
  ];
  const yearFromOptions: SelectOption[] = [
    { value: '', label: 'Any Year' },
    ...years.map((y) => ({ value: y, label: y })),
  ];
  const yearToOptions: SelectOption[] = yearFromOptions;

  /** Secondary filters surfaced via "+ More Filters". Order = menu order. */
  const SECONDARY_FILTERS: SecondaryFilter[] = [
    { id: 'country', label: 'Country of Origin' },
    { id: 'language', label: 'Language' },
    { id: 'yearMin', label: 'Released From' },
    { id: 'yearMax', label: 'Released To' },
  ];

  /** Prefix shown on a set secondary filter ("Country: Japan"). */
  const SECONDARY_SHORT: Record<string, string> = {
    country: 'Country',
    language: 'Language',
    yearMin: 'From',
    yearMax: 'To',
  };

  const SECONDARY_META: Record<
    string,
    {
      options: SelectOption[];
      searchable?: boolean;
      key: keyof LeaderboardFilters;
      icon: ReactNode;
      multi: boolean;
    }
  > = {
    country: {
      options: countryOptions,
      searchable: true,
      key: 'country',
      icon: <MapPin size={14} />,
      multi: true,
    },
    language: {
      options: languageOptions,
      searchable: true,
      key: 'language',
      icon: <Languages size={14} />,
      multi: true,
    },
    yearMin: {
      options: yearFromOptions,
      key: 'yearMin',
      icon: <Calendar size={14} />,
      multi: false,
    },
    yearMax: { options: yearToOptions, key: 'yearMax', icon: <Calendar size={14} />, multi: false },
  };

  const removeSecondary = (id: string) => {
    const meta = SECONDARY_META[id];
    if (meta) {
      if (meta.multi) setArr(meta.key as 'country' | 'language')([]);
      else set(meta.key as 'exclusivity' | 'yearMin' | 'yearMax')('');
    }
    setActiveSecondary((prev) => prev.filter((x) => x !== id));
  };

  /** Sort-by options, mirroring the sortable columns. */
  /** Sort-by options = the sortable columns, so a header click always has a
      matching dropdown entry (a missing one left the dropdown blank). */
  const SORT_OPTIONS: SelectOption[] = COLUMN_DEFS.filter((c) => c.sortField).map((c) => ({
    value: c.sortField!,
    label: c.sortField === 'title' ? 'Title (A–Z)' : c.label,
  }));

  // ─── Scope sentence ────────────────────────────────────────────────────────

  // Quarter label from the SELECTED filter, not meta (the endpoint's meta has
  // no `quarter` key — it returns `time` — so meta.quarter always fell back).
  const total = meta?.count ?? 0;

  /* The scope sentence names EVERY active filter and golds each selected value,
     so the sentence is a faithful readout of the query. A gold span marks a
     value the user chose; default (all/worldwide/latest) words stay muted. */
  const Gold = ({ children }: { children: ReactNode }) => (
    <span className={styles.scopeValue}>{children}</span>
  );

  /**
   * One sentence, noun phrase first:
   *   "6,530 exclusive Animation or Biography titles from Japan on Netflix,
   *    worldwide, in the latest quarter (Q2 2026), released 2010–2020"
   * Gold = a value the user chose. Defaults (all platforms / worldwide / latest
   * quarter) stay muted, even when they name a concrete value like Q2 2026.
   */
  const scopeNode = (() => {
    /* "A", "A or B", "A, B or C" — multi-select filters match ANY of the values. */
    const anyOf = (labels: string[]) =>
      labels.map((l, i) => (
        <span key={i}>
          {i === 0 ? '' : i === labels.length - 1 ? ' or ' : ', '}
          <Gold>{l}</Gold>
        </span>
      ));
    const sep = (nodes: ReactNode[], by: string) =>
      nodes.map((n, i) => (
        <span key={i}>
          {i > 0 ? by : ''}
          {n}
        </span>
      ));

    // Noun phrase: [count] [exclusive] [genres] [titles|shows|movies]
    const noun: ReactNode[] = [];
    if (total > 0) noun.push(<strong>{total.toLocaleString()}</strong>);
    if (filters.exclusivity)
      noun.push(<Gold>{filters.exclusivity === 'true' ? 'exclusive' : 'non-exclusive'}</Gold>);
    if (filters.genre.length) noun.push(<>{anyOf(filters.genre.map(titleCase))}</>);
    noun.push(
      filters.contentType ? <Gold>{CONTENT_TYPE_NOUN[filters.contentType]}</Gold> : 'titles',
    );
    if (total <= 0 && noun.length === 1) noun.unshift('All');

    // Qualifiers, each its own clause
    const where: ReactNode[] = [];
    if (filters.country.length)
      where.push(<>from {anyOf(filters.country.map((c) => safeName(regionNames, c)))}</>);
    if (filters.language.length)
      where.push(<>in {anyOf(filters.language.map((l) => safeName(languageNames, l)))}</>);
    where.push(
      filters.platform.length ? (
        <>on {anyOf(filters.platform.map(shortPlatform))}</>
      ) : (
        'on all platforms'
      ),
    );

    const clauses: ReactNode[] = [
      <>
        {sep(noun, ' ')}
        {where.length ? ' ' : ''}
        {sep(where, ' ')}
      </>,
      filters.region.length ? <>in {anyOf(filters.region)}</> : 'in all regions',
      isAllTime ? (
        <>
          for <Gold>all time to date</Gold>
        </>
      ) : filters.quarter ? (
        <>
          in <Gold>{prettyQuarter(filters.quarter)}</Gold>
        </>
      ) : (
        <>
          in the latest quarter{opts?.quarters?.[0] ? ` (${prettyQuarter(opts.quarters[0])})` : ''}
        </>
      ),
    ];
    if (filters.yearMin && filters.yearMax)
      clauses.push(
        <>
          released{' '}
          <Gold>
            {filters.yearMin}–{filters.yearMax}
          </Gold>
        </>,
      );
    else if (filters.yearMin)
      clauses.push(
        <>
          released since <Gold>{filters.yearMin}</Gold>
        </>,
      );
    else if (filters.yearMax)
      clauses.push(
        <>
          released up to <Gold>{filters.yearMax}</Gold>
        </>,
      );

    return <>{sep(clauses, ', ')}</>;
  })();

  // ─── Render ────────────────────────────────────────────────────────────────

  /** One renderer per column id, so the body stays declarative. */
  const renderCell = (col: ColumnDef, entry: LeaderboardEntry, index: number) => {
    const bar = (value: number | undefined) =>
      col.magnitude && value != null ? value / (maxima[col.id] ?? 1) : undefined;
    const text = (value: unknown) =>
      value == null || value === '' ? <span className={styles.noValue}>—</span> : String(value);

    switch (col.id) {
      case 'rank':
        return <span className={styles.rank}>{index + 1}</span>;

      case 'title':
        return (
          <TitleCell
            parrotId={entry.parrot_id}
            title={entry.title}
            meta={`${entry.release_year ? `${entry.release_year} • ` : ''}${titleCase(entry.main_genre || entry.content_type)}`}
          />
        );

      case 'platform': {
        const plats = platformsByRevenue(
          entry.platforms && entry.platforms.length
            ? entry.platforms
            : [entry.availability_platform],
          entry.revenue_by_platform,
        );
        if (plats.length === 1) {
          // Single platform: icon + name on one line
          return (
            <span className={styles.platformCell}>
              <PlatformIcon name={plats[0]} size={20} />
              <span className={styles.platformNames}>{shortPlatform(plats[0])}</span>
            </span>
          );
        }
        // Multi-platform: count on line 1; top two by revenue + "+n" on line 2.
        // The tooltip lists them all, in the same revenue order.
        const extra = plats.length - 2;
        return (
          <Tooltip
            title="Platforms"
            content={plats.map(shortPlatform).join(', ')}
            placement="bottom"
          >
            <span className={styles.platformCellMulti}>
              <span className={styles.platformCountRow}>
                <span className={styles.platformIcons}>
                  {plats.slice(0, 3).map((pl) => (
                    <PlatformIcon key={pl} name={pl} size={16} />
                  ))}
                </span>
                <span className={styles.platformCount}>{plats.length} Platforms</span>
              </span>
              <span className={styles.platformNames}>
                {plats.slice(0, 2).map(shortPlatform).join(', ')}
                {extra > 0 && <span className={styles.platformMore}> +{extra}</span>}
              </span>
            </span>
          </Tooltip>
        );
      }

      case 'revenue':
        return (
          <RevenueSplitCell
            total={entry.revenue_contribution_usd}
            split={entry.revenue_by_platform}
            fraction={bar(entry.revenue_contribution_usd)}
          />
        );

      case 'share':
        return (
          <MetricCell
            value={formatShare(entry.revenue_share)}
            fraction={bar(entry.revenue_share)}
            tone="share"
          />
        );

      case 'acquired':
        return (
          <MetricCell
            value={formatUsers(entry.subscribers_acquired)}
            fraction={bar(entry.subscribers_acquired)}
            tone="acquired"
          />
        );

      case 'retained':
        return (
          <MetricCell
            value={formatUsers(entry.subscribers_retained)}
            fraction={bar(entry.subscribers_retained)}
            tone="retained"
          />
        );

      case 'qoq': {
        const change = entry.revenue_change_pct;
        if (change == null) {
          return (
            <span className={styles.noValue} title="No previous period in this scope">
              —
            </span>
          );
        }
        return <Delta value={change} digits={Math.abs(change) >= 100 ? 0 : 1} max={QOQ_CAP} />;
      }

      case 'acquiredRev':
        return (
          <MetricCell
            value={formatRevenue(entry.acquired_usd ?? 0)}
            fraction={bar(entry.acquired_usd)}
            tone="acquired"
          />
        );

      case 'retainedRev':
        return (
          <MetricCell
            value={formatRevenue(entry.retained_usd ?? 0)}
            fraction={bar(entry.retained_usd)}
            tone="retained"
          />
        );

      case 'split': {
        const total = (entry.acquired_usd ?? 0) + (entry.retained_usd ?? 0);
        if (total <= 0) return <span className={styles.noValue}>—</span>;
        const acq = ((entry.acquired_usd ?? 0) / total) * 100;
        return (
          <MetricCell
            value={`${acq.toFixed(0)} / ${(100 - acq).toFixed(0)}`}
            fraction={acq / 100}
            tone="acquired"
            sub="acq / ret"
          />
        );
      }

      case 'subsEquivalent':
        return (
          <MetricCell
            value={formatUsers(entry.subscribers_equivalent)}
            fraction={bar(entry.subscribers_equivalent)}
            tone="neutral"
          />
        );

      case 'demand':
        return entry.demand_label ? (
          <span className={`${styles.tier} ${styles[`tier${tierKey(entry.demand_label)}`] ?? ''}`}>
            {entry.demand_label}
          </span>
        ) : (
          <span className={styles.noValue}>—</span>
        );

      case 'genre':
        return text(entry.main_genre && titleCase(entry.main_genre));
      case 'subGenre':
        return text(entry.sub_genre && titleCase(entry.sub_genre));
      case 'contentType':
        return text(titleCase(entry.content_type));
      case 'releaseYear':
        return text(entry.release_year);
      case 'country':
        return text(entry.original_country && safeName(regionNames, entry.original_country));
      case 'language':
        return text(entry.original_language && safeName(languageNames, entry.original_language));
      case 'imdb':
        return text(entry.imdb_rating ? entry.imdb_rating.toFixed(1) : null);
      case 'episodes':
        return text(entry.total_episodes ? entry.total_episodes.toLocaleString() : null);
      case 'seriesType':
        return text(entry.tv_series_type && titleCase(entry.tv_series_type));
      case 'exclusive':
        return entry.is_exclusive ? (
          <span className={styles.exclusiveTag}>Exclusive</span>
        ) : (
          <span className={styles.noValue}>—</span>
        );

      default:
        return null;
    }
  };

  return (
    <div className={styles.page}>
      {/* Define → Refine → meta, mirroring the licensing-intelligence Titles bar. */}
      <header className={styles.header}>
        {/* Row 1 — Define the universe */}
        <div className={styles.defineRow}>
          {portfolioId ? (
            /* A custom portfolio scopes the board instead of content type — the
               two are mutually exclusive. Disable the control and explain how to
               get it back, with a one-click "Clear portfolio" escape hatch. The
               tooltip wraps a span (not the control) so it works over a disabled
               control cleanly. */
            <Tooltip
              placement="bottom"
              title="Content type unavailable"
              content={
                <span className={styles.ctClearTip}>
                  To filter by content type, clear your Custom Portfolio selection
                  <button type="button" className={styles.ctClearBtn} onClick={clearPortfolio}>
                    Clear portfolio
                  </button>
                </span>
              }
            >
              <span>
                <SegmentedControl
                  variant="field"
                  options={CONTENT_TYPE_OPTIONS}
                  value={filters.contentType}
                  defaultValue=""
                  onChange={set('contentType')}
                  disabled
                />
              </span>
            </Tooltip>
          ) : (
            <SegmentedControl
              variant="field"
              options={CONTENT_TYPE_OPTIONS}
              value={filters.contentType}
              defaultValue=""
              onChange={set('contentType')}
              disabled={false}
            />
          )}
          {/* The content-type universe (All/TV/Movies) and a custom portfolio are
              ALTERNATIVE ways to scope the leaderboard — hence the "OR" label.
              Selecting a portfolio disables the content-type control (above);
              clearing it re-enables it. */}
          <span className={styles.orSep} aria-hidden="true">
            OR
          </span>
          {/* Portfolio scope — single-select, opt-in (none selected by default). */}
          <PortfolioPicker
            portfolios={portfolios}
            value={portfolioId}
            onChange={setPortfolioId}
            onClear={clearPortfolio}
            currentUserEmail={userEmail ?? undefined}
            loading={portfoliosPending}
            error={portfoliosError}
            className={styles.portfolioPicker}
          />
          <Select
            multiple
            values={filters.platform}
            onValuesChange={setArr('platform')}
            options={platformOptions}
            placeholder={'All Platforms'}
            label={'Platform'}
            highlightWhenSet
            searchable
            icon={
              filters.platform.length === 1 ? (
                <PlatformIcon name={filters.platform[0]} size={20} />
              ) : (
                <MonitorPlay size={14} />
              )
            }
            onClear={() => setArr('platform')([])}
            className={styles.defineSelect}
          />
          <Select
            multiple
            values={filters.region}
            onValuesChange={setArr('region')}
            options={regionOptions}
            placeholder={'All Regions'}
            label={'Region'}
            highlightWhenSet
            icon={<Globe size={14} />}
            onClear={() => setArr('region')([])}
            className={styles.defineSelect}
          />
          <Select
            value={filters.quarter}
            options={quarterOptions}
            onChange={set('quarter')}
            highlightWhenSet
            icon={<CalendarDays size={14} />}
            onClear={() => set('quarter')('')}
            className={styles.defineSelect}
          />
        </div>

        {/* Row 2 — Refine (filters). Genre is primary; the rest come via More Filters. */}
        <div className={styles.refineRow}>
          <Select
            multiple
            values={filters.genre}
            onValuesChange={setArr('genre')}
            options={genreOptions}
            placeholder={'All Genres'}
            label={'Genre'}
            highlightWhenSet
            searchable
            icon={<Clapperboard size={14} />}
            onClear={() => setArr('genre')([])}
            className={styles.refineSelect}
          />
          {/* Exclusivity — one on/off chip, styled like the filter selects. */}
          <button
            type="button"
            aria-pressed={filters.exclusivity === 'true'}
            className={`${styles.toggleChip} ${filters.exclusivity === 'true' ? styles.toggleChipOn : ''}`}
            onClick={() => set('exclusivity')(filters.exclusivity === 'true' ? '' : 'true')}
          >
            <BadgeCheck size={14} className={styles.toggleChipIcon} />
            Exclusive only
          </button>
          {activeSecondary.map((id) => {
            const meta = SECONDARY_META[id];
            const def = SECONDARY_FILTERS.find((f) => f.id === id);
            if (!meta || !def) return null;
            return (
              <DismissibleFilter
                key={id}
                options={meta.options}
                value={meta.multi ? '' : (filters[meta.key] as string)}
                onChange={
                  meta.multi ? () => {} : set(meta.key as 'exclusivity' | 'yearMin' | 'yearMax')
                }
                multiple={meta.multi}
                values={meta.multi ? (filters[meta.key] as string[]) : undefined}
                onValuesChange={meta.multi ? setArr(meta.key as 'country' | 'language') : undefined}
                searchable={meta.searchable}
                icon={meta.icon}
                onDismiss={() => removeSecondary(id)}
                label={SECONDARY_SHORT[id]}
                className={styles.refineSelect}
              />
            );
          })}
          <AddFilterChip
            filters={SECONDARY_FILTERS}
            activeIds={activeSecondary}
            onAdd={(id) => setActiveSecondary((prev) => [...prev, id])}
          />
        </div>

        {/* Row 3 — count (left) · sort + columns (right) */}
        <div className={styles.metaRow}>
          <p className={styles.subtitle}>{scopeNode}</p>

          <div className={styles.metaControls}>
            <Select
              value={sortField}
              options={SORT_OPTIONS}
              onChange={onSortSelect}
              icon={<ArrowUpDown size={14} />}
              className={styles.sortSelect}
            />
            <ColumnPicker
              columns={orderedColumns.map((c) => ({
                id: c.id,
                label: c.label === '#' ? 'Rank' : c.label,
                locked: c.locked,
              }))}
              visible={visibleColumns}
              onChange={setVisibleColumns}
              onReorder={setColumnOrder}
              onReset={() => {
                setColumnOrder(DEFAULT_ORDER);
                setVisibleColumns(DEFAULT_VISIBLE);
              }}
            />
          </div>
        </div>
      </header>

      {portfolioTooLarge && (
        <div className={styles.notice} role="alert">
          <strong>Portfolio too large to scope.</strong> This portfolio has more than 5,000 titles,
          which exceeds the leaderboard limit. Pick a smaller portfolio or clear the portfolio
          filter to see the global leaderboard.
        </div>
      )}

      {error && (
        <div className={styles.notice} role="alert">
          <strong>Failed to load the leaderboard.</strong>{' '}
          {(error as Error).message || 'The request did not complete.'} If the API server was
          restarted mid-request, reload the page.
        </div>
      )}

      {(isLoading || isResolvingMembers || rows.length > 0) && (
        <div className={styles.tableWrapper}>
          <LeaderboardTable
            columns={shownColumns.map((col) => {
              const isTitle = col.id === 'title';
              return {
                id: col.id,
                label: isTitle ? (
                  <span className={styles.titleHead}>
                    <span>Title</span>
                    <Input
                      size={'sm'}
                      className={styles.titleSearch}
                      iconLeft={<Search size={15} />}
                      value={filters.search}
                      onChange={(e) => set('search')(e.currentTarget.value)}
                      onClear={() => set('search')('')}
                      placeholder={`Search ${filters.contentType ? CONTENT_TYPE_NOUN[filters.contentType] : 'titles'}`}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </span>
                ) : (
                  col.label
                ),
                width: col.width,
                align: col.align,
                // Title hosts the search box, so it isn't a sort target here
                // (sorting by title stays available via the Sort by dropdown).
                sortKey: isTitle ? undefined : col.sortField,
                sortFirst:
                  col.sortField === 'title' || col.sortField === 'availability_platform'
                    ? ('asc' as const)
                    : ('desc' as const),
                help: col.help,
                // Title = poster + name (media); rank = narrow; rest = bars.
                skeleton: isTitle
                  ? ('media' as const)
                  : col.id === 'rank'
                    ? ('short' as const)
                    : ('bar' as const),
                render: (entry: LeaderboardEntry, idx: number) => renderCell(col, entry, idx),
              };
            })}
            rows={rows}
            getRowKey={(entry) => entry.parrot_id}
            sort={sortField ? { key: sortField, direction: sortDir } : undefined}
            onSortChange={onTableSort}
            loading={tableLoading}
          />

          {/* Scroll sentinel — pulls the next page in before it's reached */}
          <div ref={onSentinel} className={styles.sentinel}>
            {isFetchingNextPage && (
              <span className={styles.sentinelText}>
                <Spinner size="sm" /> Loading more…
              </span>
            )}
            {!hasNextPage && rows.length >= PAGE_SIZE && (
              <span className={styles.sentinelText}>
                All {total.toLocaleString()} titles loaded
              </span>
            )}
          </div>
        </div>
      )}

      {!tableLoading && !error && !portfolioTooLarge && rows.length === 0 && (
        <div className={styles.empty}>
          <h3 className={styles.emptyTitle}>No titles match this selection</h3>
          <p className={styles.emptyBody}>
            {portfolioActive && portfolioEmpty
              ? 'This portfolio has no titles in the streaming-economics dataset. Try another portfolio or clear the portfolio filter.'
              : 'Nothing found for this selection. Try widening the region or time period.'}
          </p>
        </div>
      )}
    </div>
  );
}
